import { useState, useRef, useEffect } from 'react';
import { FaFileAlt, FaTh, FaBook, FaBox, FaGripLines } from 'react-icons/fa';
import { getEntry, createEntry, addWorkspaceEntryRef } from 'lib/api';
import MoveableEntry from 'components/MoveableEntry';
import { EntryType, EntryModel, WorkspaceData, WorkspaceEntryRef } from 'lib/models';
import WorkspaceNavigation from './WorkspaceNavigation';
import ActionDrawer from 'components/ActionDrawer';
import ChatBar from 'components/ChatBar';
import EmptyWorkspace from './EmptyWorkspace';

const SCROLL_SPEED = 10;
const INIT_Z_INDEX = 1000;

interface WorkspaceMobileProps {
  workspaceUuid: string;
  onCreateNewEntry: (entryType: EntryType, x: number, y: number) => Promise<{ entry: EntryModel, ref: WorkspaceEntryRef }|undefined>;
  entriesMap: {[entryUuid: string]: EntryModel};
  entryRefsMap: {[entryRefId: string]: WorkspaceEntryRef};
  onArchiveEntry: (refId: string, entryUuid: string) => Promise<void>;
}

function WorkspaceMobile({
  entriesMap,
  onArchiveEntry,
  onCreateNewEntry,
}: WorkspaceMobileProps) {
  const [draggedEntry, setDraggedEntry] = useState<EntryModel | null>(null);

  const handleDragStart = (e: React.DragEvent, entry: EntryModel) => {
      setDraggedEntry(entry);
  };

  const handleDragOver = (e: React.DragEvent, entry: EntryModel) => {
      e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetEntry: EntryModel) => {
      e.preventDefault();
      if (draggedEntry && draggedEntry.uuid !== targetEntry.uuid) {
           const draggedRef = Object.values(entryRefsMap).find(ref => ref.entryUuid === draggedEntry.uuid);
           const targetRef = Object.values(entryRefsMap).find(ref => ref.entryUuid === targetEntry.uuid);
           if (draggedRef && targetRef) {
               const newDraggedIdx = targetRef.mobileIdx ?? 0;
               const newTargetIdx = draggedRef.mobileIdx ?? 0;
               // Call the API to update the mobileIdx field.
               await updateWorkspaceEntryRef(workspaceUuid, draggedRef.refId, draggedRef.x, draggedRef.y, draggedRef.z_index, draggedRef.width || 300, draggedRef.height || 200, newDraggedIdx);
               await updateWorkspaceEntryRef(workspaceUuid, targetRef.refId, targetRef.x, targetRef.y, targetRef.z_index, targetRef.width || 300, targetRef.height || 200, newTargetIdx);
           }
      }
      setDraggedEntry(null);
  };

  const getEntryRow = (entry: EntryModel) => {
    let Icon;
    let metaInfo = "";

    switch(entry.data.type) {
      case EntryType.TEXT:
        Icon = FaFileAlt;
        metaInfo = `${entry.data.title || "Text Entry"}`;
        break;
      case EntryType.WORKSPACE:
        Icon = FaTh;
        metaInfo = `${entry.data.name} (${entry.data.entryRefs ? Object.keys(entry.data.entryRefs).length : 0})`;
        break;
      case EntryType.JOURNAL:
        Icon = FaBook;
        metaInfo = `${entry.data.title || "Journal"}`;
        break;
      case EntryType.BUCKET:
        Icon = FaBox;
        metaInfo = `${entry.data.name || "Bucket"}`;
        break;
      default:
        Icon = FaFileAlt;
        metaInfo = ``;
    }
    const date = entry.updatedAt || entry.createdAt;
    const formattedDate = new Date(date).toLocaleDateString(
      undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    return (
      <div className="p-4 flex items-center" draggable
           onDragStart={(e) => handleDragStart(e, entry)}
           onDragOver={(e) => handleDragOver(e, entry)}
           onDrop={(e) => handleDrop(e, entry)}>
        <div className="mr-2 cursor-move">
          <FaGripLines />
        </div>
        <div className="text-palette-2">
          <Icon className="mr-4" />
        </div>
        <div>
          <div className="text-s text-palette-2">{metaInfo}</div>
          <div className="text-xs">{formattedDate}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col justify-center items-center h-full w-full relative">
      <div className="w-full h-full">
        {Object.values(entriesMap).map((entry) =>
            <div className="bg-palette-5 text-palette-4 w-full">
              {getEntryRow(entry)}
              <div className="w-full h-[1px] bg-palette-3" />
            </div>
        )}
      </div>
      <div className="w-full absolute max-w-6xl bottom-0 overflow-y-auto">
        <ChatBar />
      </div>
    </div>
  );
}

export default WorkspaceMobile;
